import L from 'leaflet';
import { useCallback, useEffect } from 'react';
import { MapContainer, Marker, TileLayer, Tooltip, useMap } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';

import 'leaflet/dist/leaflet.css';

import tailwindConfig from '@/../tailwind.config';
import { Loading } from '@/components/loading';
import { CarbonStockKeyTable } from '@/components/tables/farm-map-tables.component';
import { ProjectMapTable } from '@/components/tables/project-map-tables.component';
import { H3 } from '@/components/typography';
import { cn } from '@/lib/utils';
import { ProjectReportingType } from '@/modules/Customers/Customers.api';
import { useProjectMap } from '@/modules/Projects/Projects.api';
import { toast } from 'react-toastify';
import resolveConfig from 'tailwindcss/resolveConfig';

const fullConfig = resolveConfig(tailwindConfig);

const tooltipStyle = `
  .leaflet-marker-icon {
    transition: z-index 0.5s ease;
    cursor: pointer;
  }
  .leaflet-marker-icon.highlighted {
    z-index: 1000 !important;
  }
  .leaflet-tooltip {
    border: none ;
    background: transparent ;
    box-shadow: none ;
    z-index: 1001 !important; 
  }
  .leaflet-tooltip-top {
    margin-top: -52px ;
  }
  .leaflet-tooltip-top:before {
    border-top-color: white ;
    margin-top: -1px; 
  }
  .leaflet-tooltip-bottom:before {
    border-bottom-color: white ;
  }
`;

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = tooltipStyle;
  document.head.appendChild(style);
}

export const getFarmIdSuffix = (farmId: string): string => {
  return farmId.slice(-3);
};

const getMarkerIcon = (status: string | undefined, farmId: string) => {
  const colors = {
    HIGH: fullConfig.theme.colors.green,
    MEDIUM: fullConfig.theme.colors.yellow,
    LOW: fullConfig.theme.colors.red,
  };
  const defaultColor = fullConfig.theme.colors.neutral['450'];
  const color = colors[status?.toUpperCase() as keyof typeof colors] || defaultColor;

  const label = getFarmIdSuffix(farmId);

  const markerHtml = `
    <div style="position: relative; width: 24px; height: 41px;">
      <svg width="24" height="41" viewBox="0 0 24 41" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 0.5C5.5 0.5 0.25 5.75 0.25 12.25C0.25 21.5 12 40.5 12 40.5C12 40.5 23.75 21.5 23.75 12.25C23.75 5.75 18.5 0.5 12 0.5Z" fill="${color}" stroke="white" stroke-width="1.5"/>
      </svg>
      <div style="
        position: absolute;
        top: 6px;
        left: 0;
        right: 0;
        text-align: center;
        color: white;
        font-size: 10px;
        font-weight: bold;
        text-shadow: 0 0 2px rgba(0,0,0,0.7);
        pointer-events: none;
      ">${label}</div>
    </div>
  `;

  return L.divIcon({
    className: 'custom-marker',
    html: markerHtml,
    iconSize: [24, 41],
    iconAnchor: [12, 41],
    popupAnchor: [0, 0],
  });
};

type Props = {
  customerId: string;
  projectId: string;
  reportingType?: ProjectReportingType;
};

const MapInitializer = ({ center, zoom }: { center: [number, number]; zoom: number }) => {
  const map = useMap();

  useEffect(() => {
    map.setView(center, zoom);
  }, [map, center, zoom]);

  return null;
};

export const ProjectDetailsMap = ({ customerId, projectId, reportingType }: Props) => {
  const { data, status } = useProjectMap(projectId || '');
  const navigate = useNavigate();

  const handleMarkerClick = useCallback(
    (farmId: string) => {
      if (!customerId || !projectId || reportingType === 'project_only') return;
      navigate(`/customers/${customerId}/projects/${projectId}/farms/${farmId}`);
    },
    [customerId, projectId, navigate, reportingType]
  );

  if (status === 'error') {
    console.error('Error fetching project map data:', data);
    toast.error('Failed to retrieve map data. Please try again.');
    return null;
  }

  if (status === 'pending' || !data) {
    return <Loading />;
  }

  const center: [number, number] = [data?.center?.lat || 0, data?.center?.long || 0];
  const zoom = data?.zoom || 12;

  return (
    <div className={cn('flex-col pb-10')}>
      <H3 className="mb-5">Project Map</H3>
      <CarbonStockKeyTable />
      <div className={cn('flex h-full w-full aspect-square max-h-[615px] my-5')}>
        <MapContainer center={center} zoom={zoom} scrollWheelZoom={false} className="h-full w-full">
          <MapInitializer center={center} zoom={zoom} />
          <TileLayer
            attribution='&copy; <a href="https://www.arcgis.com/">Esri</a>'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
          {data.valid_farms?.map((farm) => {
            if (!farm.farm_latitude || !farm.farm_longitude) return null;

            const position: [number, number] = [farm.farm_latitude, farm.farm_longitude];
            const cstockStatus = farm.cstock_comment || 'UNKNOWN';

            return (
              <Marker
                key={farm.farm_id}
                position={position}
                icon={getMarkerIcon(cstockStatus, farm.farm_id || '')}
                eventHandlers={{
                  click: () => handleMarkerClick(farm.farm_id || ''),
                  mouseover: (e) => {
                    const marker = e.target;
                    const icon = marker.getElement();
                    if (icon) {
                      icon.classList.add('highlighted');
                    }
                    if (marker && typeof marker.bringToFront === 'function') {
                      marker.bringToFront();
                    }
                  },
                  mouseout: (e) => {
                    const icon = e.target.getElement();
                    if (icon) {
                      icon.classList.remove('highlighted');
                    }
                  },
                }}
                zIndexOffset={100}
              >
                <Tooltip direction="top" opacity={1} className="custom-tooltip">
                  <div className="min-w-[180px] p-2 bg-white rounded shadow-lg transition-all duration-200 ease-in-out transform hover:scale-105">
                    <div className="font-medium">
                      Farm ID: <span className="font-normal">{farm.farm_id || 'N/A'}</span>
                    </div>
                    <div className="font-medium">
                      Carbon Stock: <span className="font-normal">{cstockStatus.charAt(0) + cstockStatus.slice(1).toLowerCase()}</span>
                    </div>
                    <div className="font-medium">
                      Latitude: <span className="font-normal">{Number(farm.farm_latitude)}</span>
                    </div>
                    <div className="font-medium">
                      Longitude: <span className="font-normal">{Number(farm.farm_longitude)}</span>
                    </div>
                  </div>
                </Tooltip>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
      <ProjectMapTable data={data} customerId={customerId} projectId={projectId} reportingType={reportingType} />
    </div>
  );
};
